import { 
  LayoutDashboard, Users, Store, PackageCheck, UserCheck, 
  BarChart3, Grid3x3, MapPin, BookOpen, ShoppingCart, 
  MessageSquare, TrendingUp, DollarSign, AlertTriangle,
  Megaphone, Languages, FileText, Shield, ClipboardList,
  Flag, Package, Settings, Wrench, Star, Upload, Image, Smartphone,
  Hotel, Bed, Calendar, Activity as ProviderIcon, CreditCard, Search
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';

const adminMenuItems = [
  {
    group: 'Overview',
    items: [
      { title: 'Dashboard', url: '/admin', icon: LayoutDashboard, end: true },
      { title: 'Analytics', url: '/admin/analytics', icon: BarChart3 },
    ]
  },
  {
    group: 'User Management',
    items: [
      { title: 'Users', url: '/admin/users', icon: Users },
      { title: 'Sellers & Shops', url: '/admin/sellers', icon: Store },
      { title: 'Partners CRM', url: '/admin/partners', icon: TrendingUp },
    ]
  },
  {
    group: 'Content',
    items: [
      { title: 'Categories', url: '/admin/categories', icon: Grid3x3 },
      { title: 'Regions', url: '/admin/regions', icon: MapPin },
      { title: 'Stories', url: '/admin/stories', icon: BookOpen },
      { title: 'Blog', url: '/admin/blog', icon: FileText },
      { title: 'Reviews', url: '/admin/reviews', icon: Star },
    ]
  },
  {
    group: 'Commerce',
    items: [
      { title: 'Orders', url: '/admin/orders', icon: ShoppingCart },
      { title: 'Products', url: '/admin/products', icon: PackageCheck },
      { title: 'Collaboration Bundles', url: '/admin/collaboration-bundles', icon: Package },
      { title: 'Returns', url: '/admin/returns', icon: Package },
      { title: 'Payouts', url: '/admin/payouts', icon: DollarSign },
      { title: 'Disputes', url: '/admin/disputes', icon: AlertTriangle },
    ]
  },
  {
    group: 'Travel / Stays',
    items: [
      { title: 'Properties', url: '/admin/travel/properties', icon: Hotel },
      { title: 'Rooms & Rates', url: '/admin/travel/rooms-rates', icon: Bed },
      { title: 'Bookings', url: '/admin/travel/bookings', icon: Calendar },
      { title: 'Providers & Integrations', url: '/admin/travel/providers', icon: ProviderIcon },
    ]
  },
  {
    group: 'Marketing',
    items: [
      { title: 'Banners', url: '/admin/banners', icon: Megaphone },
      { title: 'Search Shortcuts', url: '/admin/search-shortcuts', icon: Search },
      { title: 'Campaigns', url: '/admin/marketing', icon: MessageSquare },
    ]
  },
  {
    group: 'Settings',
    items: [
      { title: 'Platform Settings', url: '/admin/settings', icon: Settings },
      { title: 'Payment Status', url: '/admin/payments-status', icon: CreditCard },
      { title: 'Translations', url: '/admin/translations', icon: Languages },
      { title: 'Security', url: '/admin/security', icon: Shield },
      { title: 'Moderation', url: '/admin/moderation', icon: Flag },
      { title: 'Integrations', url: '/admin/integrations', icon: Wrench },
    ]
  },
  {
    group: 'Tools',
    items: [
      { title: 'Image Processing', url: '/admin/image-processing', icon: Image },
      { title: 'Scrape Images', url: '/admin/scrape-images', icon: Image },
      { title: 'Import Shop', url: '/admin/import-shop', icon: Upload },
      { title: 'WhatsApp Test', url: '/admin/whatsapp-testing', icon: Smartphone },
    ]
  }
];

export function AdminSidebar() {
  const { state, isMobile } = useSidebar();
  const isCollapsed = state === 'collapsed';

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="border-b border-border p-3 sm:p-4">
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            <span className="font-semibold text-foreground text-sm sm:text-base">Admin Panel</span>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="overflow-y-auto">
        {adminMenuItems.map((group) => (
          <SidebarGroup key={group.group}>
            {(!isCollapsed || isMobile) && (
              <SidebarGroupLabel className="text-xs">{group.group}</SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild>
                      <NavLink 
                        to={item.url} 
                        end={item.end}
                        className="flex items-center gap-2 sm:gap-3 hover:bg-accent/50 text-sm"
                        activeClassName="bg-accent text-accent-foreground font-medium"
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {(!isCollapsed || isMobile) && <span className="truncate">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
